<template>
  <div class="cat-editor">
    <!-- Message si aucun test CAT sélectionné -->
    <div v-if="!props.testData?._id" class="no-test-selected">
      <div class="empty-state">
        <i class="fas fa-list-alt fa-3x text-muted mb-3"></i>
        <h4 class="text-muted">Aucun test CAT sélectionné</h4>
        <p class="text-muted">Sélectionnez un test adaptatif dans la liste pour commencer l'édition.</p>
        <div class="mt-3">
          <small class="text-muted">
            <i class="fas fa-lightbulb me-2"></i>
            Astuce : Créez un nouveau test CAT avec le bouton "Create Test" et sélectionnez "Adaptive" comme progression.
          </small>
        </div>
      </div>
    </div>
    
    <!-- Interface CAT normale -->
    <div v-else>
    <div class="cat-header">
      <div class="cat-main-info">
        <h3>🐈 CAT Editor - {{ props.testData.title }}</h3>
        <div class="cat-info">
          <span class="badge bg-info">{{ levels.length }} Levels</span>
          <span class="badge" :class="getTotalItems() === 0 ? 'bg-success' : 'bg-secondary'">
            {{ getTotalItems() === 0 ? '✓ Ready to Add Items' : `${getTotalItems()} Total Items` }}
          </span>
          <span class="badge" :class="props.testData.CATParameters?.randomizedItemOrder ? 'bg-success' : 'bg-warning'">
            {{ props.testData.CATParameters?.randomizedItemOrder ? 'Randomized Order' : 'Fixed Order' }}
          </span>
          <span class="badge bg-primary">
            {{ props.testData.CATParameters?.confirmation || 3 }} Confirmation{{ (props.testData.CATParameters?.confirmation || 3) > 1 ? 's' : '' }}
          </span>
        </div>
      </div>
      <div class="cat-requirements">
        <div class="requirement-info">
          <i class="fas fa-info-circle text-info me-2"></i>
          <span class="text-muted">Required per level: </span>
          <span class="fw-bold text-primary">{{ requiredItemsPerLevel }} items</span>
        </div>
        <div class="requirement-legend">
          <span class="legend-item">
            <span class="legend-color bg-success"></span>
            <small>Sufficient</small>
          </span>
          <span class="legend-item">
            <span class="legend-color bg-danger"></span>
            <small>Needs more items</small>
          </span>
        </div>
        
        <!-- Bouton de secours pour forcer la synchronisation -->
        <div class="sync-controls mt-2">
          <button 
            v-if="getTotalItems() === 0 && (testStore.testData?.elements?.length || 0) > 0"
            class="btn btn-sm btn-warning"
            @click="forceSyncDisplay"
            title="Force synchronization of items display"
          >
            <i class="fas fa-sync"></i> Sync Display
          </button>
        </div>
      </div>
    </div>

    <!-- Navigation des niveaux -->
    <div class="levels-navigation">
      <div class="levels-container">
        <div 
          v-for="(level, index) in displayLevels" 
          :key="level.id" 
          class="level-section"
          :class="{ 
            'active-level': activeLevel === index,
            'insufficient-items': getLevelStatus(index).isInsufficient && getTotalItems() > 0,
            'new-test-ready': getTotalItems() === 0
          }"
        >
          <div class="level-header" @click="setActiveLevel(index)">
            <div class="level-title">
              <h4>Level {{ index + 1 }}</h4>
              <span class="level-difficulty">{{ getDifficultyLabel(index) }}</span>
              <!-- Indicateur de statut -->
              <span 
                v-if="getLevelStatus(index).isInsufficient" 
                class="level-warning ms-2"
                title="Ce niveau a besoin de plus d'items"
              >
                <i class="fas fa-exclamation-triangle text-warning"></i>
              </span>
            </div>
            <div class="level-stats">
              <!-- Affichage du ratio avec indicateurs visuels -->
              <span 
                class="item-count-ratio"
                :class="{
                  'text-danger': getLevelStatus(index).isInsufficient,
                  'text-success': !getLevelStatus(index).isInsufficient
                }"
              >
                {{ getLevelStatus(index).ratio }} items
              </span>
              <span class="text-muted ms-1">({{ getLevelStatus(index).current }} / {{ getLevelStatus(index).required }})</span>
              <button 
                class="btn btn-sm btn-primary ms-2" 
                @click.stop="addNewItem(index)"
                title="Add new item to this level"
              >
                <i class="fas fa-plus"></i> Add Item
              </button>
            </div>
          </div>
          
          <!-- Items du niveau -->
          <div v-if="activeLevel === index" class="level-content">
            <div class="items-grid">
              <div 
                v-for="(item, itemIndex) in level.items" 
                :key="item.id"
                class="item-card"
                :class="{ 'selected-item': selectedItem?.id === item.id, 'dragging': draggedItem?.id === item.id }"
                @click="selectItem(item, index, itemIndex)"
                draggable="true"
                @dragstart="onDragStart($event, item, index, itemIndex)"
                @dragover="onDragOver($event)"
                @drop="onDrop($event, index, itemIndex)"
                @dragend="onDragEnd()"
              >
                <!-- Poignée de drag & drop (seulement si ordre fixe) -->
                <div 
                  v-if="!props.testData.CATParameters?.randomizedItemOrder" 
                  class="drag-handle" 
                  @mousedown.stop
                  title="Drag to reorder (Fixed Order mode)"
                >
                  <i class="fas fa-grip-vertical"></i>
                </div>
                
                <div class="item-content-wrapper">
                  <div class="item-header">
                    <span class="item-type">{{ item.type || 'choice' }}</span>
                    <div class="item-actions">
                      <button 
                        class="btn btn-sm btn-outline-secondary"
                        @click.stop="editItem(item, index, itemIndex)"
                        title="Edit item"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-danger"
                        @click.stop="deleteItem(index, itemIndex)"
                        title="Delete item"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div class="item-preview">
                    <h6>{{ item.el_Text || `Item ${itemIndex + 1}` }}</h6>
                    <p class="item-content">{{ getItemPreview(item) }}</p>
                  </div>
                  <div class="item-footer">
                    <small class="text-muted">{{ item.metadata?.Created || 'New item' }}</small>
                  </div>
                </div>
              </div>
              
              <!-- Bouton d'ajout rapide dans la grille -->
              <div class="item-card add-item-card" @click="addNewItem(index)">
                <div class="add-item-content">
                  <i class="fas fa-plus fa-2x"></i>
                  <p>Add New Item</p>
                </div>
              </div>
            </div>
            
            <!-- Message si pas d'items -->
            <div v-if="level.items.length === 0" class="no-items-message">
              <div class="success-state" v-if="getTotalItems() === 0">
                <i class="fas fa-rocket text-success"></i>
                <h6 class="text-success mt-2">🎉 CAT Test Successfully Created!</h6>
                <p class="text-muted">Your adaptive test structure is ready with {{ levels.length }} levels.</p>
                <p class="text-primary"><strong>Next step:</strong> Click "Add Item" to create your first question.</p>
              </div>
              <div v-else class="empty-level-state">
                <i class="fas fa-info-circle text-info"></i>
                <p class="text-muted mt-2">This level is empty. Click "Add Item" to add questions here.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section de création/édition d'item -->
    <div v-if="showItemCreation || selectedItem" class="item-editor-section">
      <!-- Mode création -->
      <div v-if="showItemCreation" class="creation-mode">
        <div class="editor-header">
          <h5>
            <i class="fas fa-plus-circle text-success"></i>
            Creating New Item for Level {{ creatingItemLevel + 1 }}
            <span class="badge bg-info ms-2">Difficulty: {{ creatingItemLevel + 1 }}</span>
          </h5>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelItemCreation">
            <i class="fas fa-times"></i> Cancel
          </button>
        </div>
        
        <!-- Composant Editor pour création -->
        <div class="item-creation-container">
          <div class="alert alert-info">
            <i class="fas fa-info-circle"></i>
            Create your new item below. The difficulty will be automatically set to Level {{ creatingItemLevel + 1 }}.
          </div>
          
          <!-- Import du composant Editor -->
          <EditorComponent 
            v-if="selectedTestElement"
            :key="`creation-${selectedTestElement.el_ID}-${selectedTestElement.el_Type || 'default'}`"
            :element="selectedTestElement"
            :preset-difficulty="creatingItemLevel + 1"
            :auto-open="true"
            @item-created="onItemCreated"
            @cancel-creation="cancelItemCreation"
          />
        </div>
      </div>
      
      <!-- Mode édition normal -->
      <div v-else-if="selectedItem && !showItemCreation" class="editing-mode">
      <div class="editor-header">
        <h5>Editing: {{ selectedItem.el_Text || `Item ${selectedItemIndex + 1}` }} 
          <span class="badge bg-secondary">Level {{ selectedLevelIndex + 1 }}</span>
        </h5>
        <button class="btn btn-sm btn-outline-secondary" @click="closeItemEditor">
          <i class="fas fa-times"></i> Close
        </button>
      </div>
      
      <!-- Interface Editor/Preview intégrée -->
      <div class="item-editor-container">
        <div class="row">
          <!-- Section Editor -->
          <div class="col-md-6 mt-2">
                <div v-if="selectedTestElement" class="editor-section">
                  <component 
                    :is="editorComponent" 
                    :key="`editor-${selectedTestElement.el_Type}-${selectedTestElement.el_ID}`"
                    :element="selectedTestElement" 
                    :is-item-bank="false" 
                  />
                </div>
                <div v-else class="alert alert-warning">
                  <i class="fas fa-exclamation-triangle"></i>
                  Item not found in test elements. This might be a CAT distribution item.
            </div>
          </div>
          
          <!-- Section Preview -->
          <div class="col-md-6 mt-2">
            <div>
              <div v-if="selectedTestElement" class="preview-section">
                  <component 
                    :is="previewComponent" 
                    :key="`preview-${selectedTestElement.el_Type}-${selectedTestElement.el_ID}`"
                    :element="selectedTestElement" 
                  />
                </div>
                <div v-else class="alert alert-info">
                  <i class="fas fa-info-circle"></i>
                  No preview available for CAT distribution items.
                </div>
              
            </div>
          </div>
        </div>
        
        <!-- CAT Item Metadata -->
        <div class="card mt-3">
          <div class="card-header">
            <h6><i class="fas fa-tags"></i> CAT Item Metadata</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label>Item Source</label>
                  <input type="text" class="form-control" 
                         :value="selectedItem.metadata?.Source || 'Unknown'" readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>Calibration Method</label>
                  <input type="text" class="form-control" 
                         :value="selectedItem.metadata?.CalibrationMethod || 'N/A'" readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>Difficulty Level</label>
                  <input type="number" class="form-control" 
                         v-model="selectedItem.difficulty" 
                         min="1" :max="props.testData.CATParameters?.paliers || 6"
                         @change="updateItemDifficulty">
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Usage Count</label>
                  <input type="number" class="form-control" 
                         :value="selectedItem.metadata?.UsageCount || 0" readonly>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Confidence Score</label>
                  <input type="number" class="form-control" 
                         :value="selectedItem.metadata?.Confidence || 0" 
                         readonly step="0.01" min="0" max="1">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent, watch, nextTick } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { useTestStore } from '../../stores/testStore'
import EditorComponent from '../interactions/default/Editor.vue'
import api from '@/services/axios'

// Import des utilitaires de debug (uniquement en développement)
if (import.meta.env.DEV) {
  import('../../utils/catTestHelper.js')
}

// Props pour recevoir les données du test
const props = defineProps({
  testData: {
    type: Object,
    default: () => ({})
  }
})

// Émettre les changements vers le parent
const emit = defineEmits(['update:testData'])

// Fonction utilitaire pour valider les ObjectIds MongoDB (sans dépendance mongoose)
const isValidObjectId = (id) => {
  // Un ObjectId MongoDB est une string hexadécimale de 24 caractères
  return typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)
}

// Fonction utilitaire pour convertir la difficulté CAT (1-6) vers l'éditeur (1-10)
const convertCATDifficultyToEditor = (item) => {
  // Essayer de récupérer la difficulté depuis différents champs
  let catDifficulty = item.creator_difficulty || item.diff_default || item.difficulty;
  
  if (!catDifficulty) {
    console.warn('⚠️ No difficulty found in CAT item, using default 5:', item);
    return 5; // Valeur par défaut de l'éditeur
  }
  
  // Si la valeur est déjà dans la plage de l'éditeur (1-10), la garder
  if (catDifficulty >= 1 && catDifficulty <= 10) {
    // Si c'est probablement une valeur CAT (1-6), la convertir
    if (catDifficulty <= 6) {
      // Conversion 1-6 vers 1-10 : (valeur-1) * 9/5 + 1
      const converted = Math.round((catDifficulty - 1) * 9 / 5) + 1;
      console.log(`🔄 Converting CAT difficulty ${catDifficulty} (1-6) → ${converted} (1-10)`);
      return Math.max(1, Math.min(10, converted));
    } else {
      // Probablement déjà dans la bonne plage
      console.log(`✅ Using existing difficulty value: ${catDifficulty}`);
      return catDifficulty;
    }
  }
  
  console.warn('⚠️ Invalid difficulty value, using default 5:', catDifficulty);
  return 5;
}

// Fonction utilitaire pour convertir la difficulté éditeur (1-10) vers CAT (1-6)
const convertEditorDifficultyToCAT = (difficulty) => {
  if (!difficulty || difficulty < 1 || difficulty > 10) {
    return 3; // Valeur par défaut CAT
  }
  
  // Conversion 1-10 vers 1-6 : (valeur-1) * 5/9 + 1
  const converted = Math.round((difficulty - 1) * 5 / 9) + 1;
  return Math.max(1, Math.min(6, converted));
}

// État de l'éditeur CAT
const activeLevel = ref(0)
const selectedItem = ref(null)
const selectedLevelIndex = ref(-1)
const selectedItemIndex = ref(-1)

// État pour la création d'items
const showItemCreation = ref(false)
const creatingItemLevel = ref(-1)

// État du drag & drop
const draggedItem = ref(null)
const draggedFromLevel = ref(-1)
const draggedFromIndex = ref(-1)

// Niveaux de difficulté (basés sur les paramètres CAT)
const levels = ref([])

// Store pour accéder aux éléments du test
const testStore = useTestStore()

// Computed pour transformer les levels (el_ID) en objets d'affichage
const displayLevels = computed(() => {
  console.log('📊 displayLevels computed called')
  console.log('   - levels.value:', levels.value?.length || 0, 'levels')
  console.log('   - testStore.testData.elements:', testStore.testData?.elements?.length || 0, 'elements')
  
  if (!levels.value || !testStore.testData?.elements) {
    console.log('⚠️ displayLevels: Missing data, returning empty array')
    return []
  }
  
  const result = levels.value.map((level, levelIndex) => {
    console.log(`📋 Processing Level ${levelIndex + 1} (${level.label}):`, level.items?.length || 0, 'item references')
    
    const levelItems = (level.items || []).map(el_ID => {
      // Trouver l'élément correspondant dans elements
      const element = testStore.testData.elements.find(el => el.el_ID === el_ID)
      
      if (!element) {
        console.warn(`⚠️ Level ${level.label}: Element not found for el_ID: ${el_ID}`)
        console.log('   Available el_IDs:', testStore.testData.elements.map(el => el.el_ID).slice(0, 5))
        return null
      }
      
      console.log(`✅ Level ${level.label}: Found element ${element.el_ID} (${element.el_Type})`)
      
      // Créer l'objet d'affichage depuis l'élément
      return {
        id: element.el_ID, // Pour la compatibilité avec le template
        el_ID: element.el_ID,
        title: element.el_Text || element.el_RichText || `Item ${element.el_ID}`,
        type: element.el_Type || 'default',
        contentPreview: getItemPreview({ 
          title: element.el_Text || element.el_RichText,
          type: element.el_Type 
        }),
        difficulty: element.difficulty_rating || level.level,
        metadata: {
          ...element.metadata,
          IsTestElement: true,
          TestElementId: element.el_ID
        }
      }
    }).filter(Boolean) // Supprimer les éléments null
    
    console.log(`🎯 Level ${level.label} final: ${levelItems.length} display items`)
    
    return {
      ...level,
      items: levelItems
    }
  })
  
  console.log('🎉 displayLevels final result:', result.map(l => `${l.label}: ${l.items.length} items`).join(', '))
  return result
})

// Computed pour récupérer l'élément sélectionné depuis le store
const selectedTestElement = computed(() => {
  // Utiliser directement l'élément sélectionné dans le testStore
  // qui a été défini par selectItem() via testStore.setSelectedElement()
  return testStore.selectedElement || null
})

// Cache des composants pour éviter les remontages
const componentCache = new Map()

// Composant Editor dynamique
const editorComponent = computed(() => {
  if (!selectedTestElement.value) return null
  
  const type = selectedTestElement.value.el_Type || 'default'
  const cacheKey = `editor-${type}`
  
  if (!componentCache.has(cacheKey)) {
    componentCache.set(cacheKey, defineAsyncComponent(() =>
      import(`@/components/interactions/${type}/Editor.vue`).catch(() =>
        import('@/components/interactions/default/Editor.vue')
      )
    ))
  }
  
  return componentCache.get(cacheKey)
})

// Composant Preview dynamique
const previewComponent = computed(() => {
  if (!selectedTestElement.value) return null
  
  const type = selectedTestElement.value.el_Type || 'default'
  const cacheKey = `preview-${type}`
  
  if (!componentCache.has(cacheKey)) {
    componentCache.set(cacheKey, defineAsyncComponent(() =>
      import(`@/components/interactions/${type}/Preview.vue`).catch(() =>
        import('@/components/interactions/default/Preview.vue')
      )
    ))
  }
  
  return componentCache.get(cacheKey)
})

// Initialisation des niveaux basée sur les paramètres CAT
const initializeLevels = () => {
  console.log('🔄 Initializing CAT levels with el_ID references...')
  console.log('📋 Elements available:', testStore.testData?.elements?.length || 0)
  
  const catParams = props.testData.CATParameters || { paliers: 4 }
  const numLevels = catParams.paliers || 4
  const elements = testStore.testData?.elements || []
  
  console.log(`⚙️ CAT Parameters:`, catParams)
  console.log(`📊 Number of levels: ${numLevels}`)
  console.log(`📊 Total elements: ${elements.length}`)
  
  // Priorité 1: Utiliser testData.levels existante (même structure que pages)
  if (props.testData.levels && props.testData.levels.length > 0) {
    console.log('📂 Loading saved testData.levels structure')
    
    levels.value = props.testData.levels.map((level, index) => {
      console.log(`  📋 ${level.label}: ${level.items?.length || 0} items`)
      return {
        id: level._id || uuidv4(),
        level: index + 1,
        difficulty: index + 1,
        label: level.label,
        randomized: level.randomized,
        items: level.items || [] // Array d'el_ID
      }
    })
    
    console.log(`✅ Loaded structure with ${levels.value.reduce((sum, level) => sum + level.items.length, 0)} item references`)
    return
  }
  
  // Priorité 2: Migrer depuis CATStructure si elle existe (rétrocompatibilité)
  if (props.testData.CATStructure && props.testData.CATStructure.levels && props.testData.CATStructure.levels.length > 0) {
    console.log('📂 Migrating from old CATStructure to new testData.levels')
    
    levels.value = props.testData.CATStructure.levels.map(level => {
      return {
        id: level.id || uuidv4(),
        level: level.level,
        difficulty: level.difficulty || level.level,
        label: `Level ${level.level}`,
        randomized: props.testData.CATParameters?.randomizedItemOrder || true,
        items: level.items || []
      }
    })
    
    // Sauvegarder immédiatement dans le nouveau format
    saveLevelsStructure()
    
    console.log(`✅ Migrated structure with ${levels.value.reduce((sum, level) => sum + level.items.length, 0)} item references`)
    return
  }
  
  // Priorité 3: Créer structure depuis les éléments existants (grouper par difficulty_rating)
  if (elements.length > 0) {
    console.log('🎯 Creating CAT structure from existing elements (grouped by difficulty)')
    
    // Créer les niveaux vides avec la nouvelle structure
    levels.value = Array.from({ length: numLevels }, (_, index) => ({
      id: uuidv4(),
      level: index + 1,
      difficulty: index + 1,
      label: `Level ${index + 1}`,
      randomized: props.testData.CATParameters?.randomizedItemOrder || true,
      items: [] // Array d'el_ID
    }))
    
    // Grouper les éléments par niveau de difficulté
    elements.forEach(element => {
      const elementDifficulty = element.difficulty_rating || element.metadata?.CATLevel || 1
      
      // Convertir difficulty_rating (1-10) vers niveau CAT (1-numLevels)
      let targetLevel
      if (elementDifficulty <= numLevels) {
        targetLevel = Math.max(1, Math.floor(elementDifficulty))
      } else {
        // Conversion proportionnelle pour difficulty_rating > numLevels
        targetLevel = Math.max(1, Math.min(numLevels, Math.ceil(elementDifficulty * numLevels / 10)))
      }
      
      console.log(`📝 Element ${element.el_ID} (difficulty: ${elementDifficulty}) → Level ${targetLevel}`)
      
      // Ajouter l'el_ID au niveau approprié
      if (levels.value[targetLevel - 1]) {
        levels.value[targetLevel - 1].items.push(element.el_ID)
      }
    })
    
    const totalAssigned = levels.value.reduce((sum, level) => sum + level.items.length, 0)
    console.log(`✅ Created structure with ${totalAssigned} elements assigned to levels`)
    
    // Forcer la réactivité Vue pour l'affichage immédiat
    levels.value = [...levels.value]
    
    // S'assurer que le premier niveau est actif pour voir les items
    if (levels.value.length > 0) {
      setActiveLevel(0)
    }
    
    return
  }
  
  // Priorité 4: Structure vide pour nouveau test CAT
  console.log('🆕 Creating empty CAT structure')
  levels.value = Array.from({ length: numLevels }, (_, index) => ({
    id: uuidv4(),
    level: index + 1,
    difficulty: index + 1,
    label: `Level ${index + 1}`,
    randomized: props.testData.CATParameters?.randomizedItemOrder || true,
    items: [] // Array d'el_ID vide
  }))
  
  console.log(`✅ Created empty structure with ${numLevels} levels`)
  
  // Forcer la réactivité Vue pour l'affichage immédiat
  levels.value = [...levels.value]
  
  // S'assurer que le premier niveau est actif
  if (levels.value.length > 0) {
    setActiveLevel(0)
  }
  
  // Sauvegarder immédiatement la structure pour un nouveau test
  const initialLevelsStructure = saveLevelsStructure()
  
  // Mettre à jour immédiatement testData pour un nouveau test
  if (!props.testData.levels || props.testData.levels.length === 0) {
    const updatedTestData = {
      ...props.testData,
      levels: initialLevelsStructure
    }
    emit('update:testData', updatedTestData)
    testStore.setTestData(updatedTestData)
    console.log('💾 Initial testData.levels structure saved')
  }
}

// Ajouter des items de démonstration
const addDemoItems = () => {
  levels.value.forEach((level, levelIndex) => {
    // Ajouter 2-3 items par niveau pour la démo
    const itemsCount = Math.floor(Math.random() * 3) + 2
    for (let i = 0; i < itemsCount; i++) {
      level.items.push(createDemoItem(levelIndex + 1, i + 1))
    }
  })
}

// Créer un item de démonstration
const createDemoItem = (levelNum, itemNum) => ({
  id: uuidv4(),
  title: `Level ${levelNum} - Item ${itemNum}`,
  type: 'choice',
  contentPreview: `This is a sample item for level ${levelNum}. It demonstrates the adaptive testing structure.`,
  metadata: {
    Created: new Date().toISOString().split('T')[0],
    Level: levelNum,
    Difficulty: levelNum
  }
})

// Computed pour calculer le nombre d'items requis par niveau
const requiredItemsPerLevel = computed(() => {
  const catParams = props.testData.CATParameters || { paliers: 4, confirmation: 3, echec: 3 }
  const paliers = parseInt(catParams.paliers) || 4
  const confirmation = parseInt(catParams.confirmation) || 3
  const echec = parseInt(catParams.echec) || 3

  // Calcul du nombre total d'items requis (même logique que CATNbQuestionsSimulator)
  const niveauDepart = Math.ceil(paliers / 2)
  const montee = paliers - niveauDepart + 1
  const descente = paliers
  const essaisParPalier = (confirmation - 1) * 2
  const totalRequired = montee * essaisParPalier + descente + (echec - 1) + confirmation

  // Répartition idéale : diviser équitablement entre les niveaux
  return Math.ceil(totalRequired / paliers)
})

// Computed pour déterminer quels levels ont besoin de plus d'items
const getLevelStatus = (levelIndex) => {
  // Avec l'architecture el_ID, compter directement depuis displayLevels
  const currentItems = displayLevels.value[levelIndex]?.items?.length || 0
  const required = requiredItemsPerLevel.value
  const isInsufficient = currentItems < required
  
  return {
    current: currentItems,
    required: required,
    isInsufficient: isInsufficient,
    ratio: `${currentItems}/${required}`
  }
}

// Méthodes de gestion des niveaux
const setActiveLevel = (index) => {
  activeLevel.value = index
  closeItemEditor()
}

const getDifficultyLabel = (index) => {
  const labels = ['Beginner', 'Easy', 'Medium', 'Hard', 'Expert', 'Master']
  return labels[index] || `Level ${index + 1}`
}

const getTotalItems = () => {
  return displayLevels.value.reduce((total, level) => total + level.items.length, 0)
}

// Méthodes de gestion des items
const addNewItem = async (levelIndex) => {
  console.log('🆕 Adding new item to level', levelIndex + 1)
  
  // Créer un nouvel élément dans le test
  const newElementId = uuidv4()
  const newTestElement = {
    el_ID: newElementId,
    el_Type: 'default',
    el_Text: `New Item - Level ${levelIndex + 1}`,
    el_RichText: '',
    options: [
      {
        id: `${newElementId}_choice1`,
        text: "Choice 1",
        isCorrect: false,
        weight: 0
      },
      {
        id: `${newElementId}_choice2`, 
        text: "Choice 2",
        isCorrect: true,
        weight: 1
      }
    ],
    multiple: false,
    maxScore: "1",
    weight: 1,
    feedback: {
      correct: "Correct",
      incorrect: "Incorrect"
    },
    gaps: [{"id": 0, "label": "GAP", "weight": 1}],
    el_GapsedText: "<p>This is an example, press the gap button to create a <span class='gap'>GAP</span>.</p>",
    labels: ["First item", "Second item", "Third item"],
    el_InteractionData: {
      expectedPairs: [
        { left: 'Left item 1', right: 'Right item 1' },
        { left: 'Left item 2', right: 'Right item 2' }
      ],
      pointsPerPair: 1
    },
    AIisActivated: false,
    correctionAxes: [],
    tip: "",
    randomized: false,
    isNewElement: true,
    language: "en",
    dimension: [],
    interactions: [],
    pages: [{
      page_ID: uuidv4(),
      page_Title: 'Page 1',
      interactions: []
    }],
    difficulty_rating: levelIndex + 1,
    metadata: {
      Created: new Date().toISOString(),
      Level: levelIndex + 1,
      Source: 'CAT_Editor',
      IsTestElement: true
    }
  }
  
  // Ajouter l'élément aux elements du test (source unique)
  if (!testStore.testData.elements) {
    testStore.testData.elements = []
  }
  testStore.testData.elements.push(newTestElement)
  
  console.log('📝 Added element to testStore:', newElementId)
  
  // Ajouter l'el_ID au niveau (référence seulement)
  if (!levels.value[levelIndex]) {
    console.error('❌ Level not found:', levelIndex)
    return
  }
  
  if (!levels.value[levelIndex].items) {
    levels.value[levelIndex].items = []
  }
  
  levels.value[levelIndex].items.push(newElementId)
  
  console.log('📋 Added el_ID to level:', {
    levelIndex: levelIndex,
    el_ID: newElementId,
    levelItemsCount: levels.value[levelIndex].items.length
  })
  
  // Forcer la réactivité Vue
  levels.value = [...levels.value]
  
  console.log('🔄 Levels array recreated for reactivity')
  
  // S'assurer que le niveau est actif pour voir l'item
  setActiveLevel(levelIndex)
  
  // Activer le mode création
  showItemCreation.value = true
  creatingItemLevel.value = levelIndex
  
  // Sélectionner le nouvel item pour l'édition
  const displayLevel = displayLevels.value[levelIndex]
  if (displayLevel && displayLevel.items.length > 0) {
    const newItem = displayLevel.items[displayLevel.items.length - 1]
    selectedItem.value = { ...newItem }
    selectedLevelIndex.value = levelIndex
    selectedItemIndex.value = displayLevel.items.length - 1
    
    console.log('📌 Item selected:', {
      selectedLevelIndex: selectedLevelIndex.value,
      selectedItemIndex: selectedItemIndex.value,
      selectedItemTitle: selectedItem.value.title
    })
    
    // Forcer la sélection dans le testStore pour que l'Editor s'affiche
    testStore.setSelectedElement(newTestElement, newItem.title)
  }
  
  // Sauvegarder automatiquement l'ajout d'item (critique pour la persistance)
  await saveItemOrder()
  
  console.log('✅ New item added and ready for editing - Item should be visible in level', levelIndex + 1)
}

const selectItem = async (item, levelIndex, itemIndex) => {
  console.log('🎯 Selecting CAT item with el_ID reference:', {
    el_ID: item.el_ID,
    title: item.title,
    levelIndex,
    itemIndex
  })
  
  selectedItem.value = { ...item }
  selectedLevelIndex.value = levelIndex
  selectedItemIndex.value = itemIndex
  
  // Avec l'architecture el_ID, chercher directement dans testStore.testData.elements
  const element = testStore.testData?.elements?.find(el => el.el_ID === item.el_ID)
  
  if (element) {
    console.log('✅ Found element in testStore:', element.el_ID)
    
    // Sélectionner l'élément dans le testStore pour l'édition
    testStore.setSelectedElement(element, item.title || `Item ${itemIndex + 1}`)
    
    console.log('📌 Element selected for editing')
  } else {
    console.error('❌ Element not found in testStore for el_ID:', item.el_ID)
    // Fallback: créer un élément temporaire pour affichage
    const tempElement = {
      el_ID: item.el_ID,
      el_Type: item.type || 'default',
      el_Text: item.title || `Item ${itemIndex + 1}`,
      difficulty_rating: item.difficulty || levelIndex + 1,
      metadata: item.metadata || {}
    }
    testStore.setSelectedElement(tempElement, item.title || `Item ${itemIndex + 1}`)
  }
  
  // Fermer le mode création s'il était ouvert
  showItemCreation.value = false
}

const editItem = (item, levelIndex, itemIndex) => {
  selectItem(item, levelIndex, itemIndex)
}

const deleteItem = async (levelIndex, itemIndex) => {
  if (confirm('Are you sure you want to delete this item?')) {
    // Avec l'architecture el_ID, récupérer l'el_ID depuis le niveau
    const el_ID = levels.value[levelIndex].items[itemIndex]
    console.log('🗑️ Deleting item with el_ID:', el_ID)
    
    // Supprimer l'el_ID du niveau CAT
    levels.value[levelIndex].items.splice(itemIndex, 1)
    
    // Supprimer l'élément du testStore.testData.elements (source de vérité)
    if (el_ID) {
      const elementIndex = testStore.testData.elements.findIndex(el => el.el_ID === el_ID)
      if (elementIndex >= 0) {
        testStore.testData.elements.splice(elementIndex, 1)
        console.log('🗑️ Removed element from testStore.elements:', el_ID)
      } else {
        console.warn('⚠️ Element not found in testStore.elements:', el_ID)
      }
    }
    
    // Si l'item supprimé était sélectionné, fermer l'éditeur
    if (selectedLevelIndex.value === levelIndex && selectedItemIndex.value === itemIndex) {
      closeItemEditor()
    }
    
    // Sauvegarder automatiquement la suppression d'item
    await saveItemOrder()
    
    console.log('✅ Item deleted and levels updated')
  }
}

const closeItemEditor = () => {
  selectedItem.value = null
  selectedLevelIndex.value = -1
  selectedItemIndex.value = -1
  
  // Fermer aussi le mode création
  showItemCreation.value = false
  creatingItemLevel.value = -1
  
  // Nettoyer la sélection dans le testStore
  testStore.clearSelectedElement()
}

// Méthodes pour la gestion de la création d'items
const cancelItemCreation = () => {
  console.log('❌ Canceling item creation')
  
  // Si on était en train de créer un item, le supprimer du niveau
  if (creatingItemLevel.value >= 0 && selectedItem.value) {
    const levelItems = levels.value[creatingItemLevel.value].items
    
    // Avec l'architecture el_ID, chercher l'index de l'el_ID dans le niveau
    const itemIndex = levelItems.findIndex(el_ID => {
      // Trouver l'item correspondant dans displayLevels pour obtenir l'ID
      const displayLevel = displayLevels.value[creatingItemLevel.value]
      if (displayLevel) {
        const displayItem = displayLevel.items.find(item => item.el_ID === el_ID)
        return displayItem && displayItem.id === selectedItem.value.id
      }
      return false
    })
    
    if (itemIndex >= 0) {
      const el_ID_to_remove = levelItems[itemIndex]
      
      // Supprimer l'el_ID du niveau
      levelItems.splice(itemIndex, 1)
      
      // Supprimer aussi l'élément du testStore.testData.elements
      if (el_ID_to_remove) {
        const elementIndex = testStore.testData.elements.findIndex(el => el.el_ID === el_ID_to_remove)
        if (elementIndex >= 0) {
          testStore.testData.elements.splice(elementIndex, 1)
          console.log('🗑️ Removed cancelled element from testStore:', el_ID_to_remove)
        }
      }
    }
  }
  
  // Fermer le mode création
  showItemCreation.value = false
  creatingItemLevel.value = -1
  selectedItem.value = null
  selectedLevelIndex.value = -1
  selectedItemIndex.value = -1
  
  testStore.clearSelectedElement()
}

const onItemCreated = async (createdElement) => {
  console.log('🎉 CATEditor: Item creation completed', {
    el_ID: createdElement.el_ID,
    el_Type: createdElement.el_Type,
    creatingItemLevel: creatingItemLevel.value,
    selectedLevelIndex: selectedLevelIndex.value,
    selectedItemIndex: selectedItemIndex.value
  })
  
  // Assurer que la difficulté est correctement définie
  createdElement.difficulty_rating = creatingItemLevel.value + 1
  
  // Mettre à jour l'élément dans le testStore (source de vérité)
  testStore.updateElement(createdElement)
  
  console.log('✅ Element updated in testStore:', {
    el_ID: createdElement.el_ID,
    el_Type: createdElement.el_Type,
    el_Text: createdElement.el_Text?.substring(0, 50),
    difficulty_rating: createdElement.difficulty_rating
  })
  
  // Avec l'architecture el_ID, l'élément est déjà référencé dans le niveau
  // displayLevels se mettra automatiquement à jour grâce au computed
  
  // Forcer la réactivité Vue en recréant l'array des levels
  levels.value = [...levels.value]
  
  // Fermer le mode création et passer en mode édition normal
  showItemCreation.value = false
  creatingItemLevel.value = -1
  
  // Sauvegarder les changements (critique pour la persistance)
  await saveItemOrder()
  
  console.log('✅ CAT item creation process completed successfully - Item should now be visible with correct type and title')
}

const getItemPreview = (item) => {
  // Essayer d'obtenir un aperçu du contenu de l'item
  if (item.contentPreview) {
    return item.contentPreview
  }
  
  // Si l'item a un titre ou du texte, l'utiliser
  if (item.title && item.title !== `New Item - Level ${item.difficulty || 1}`) {
    return item.title.substring(0, 100) + (item.title.length > 100 ? '...' : '')
  }
  
  // Chercher dans l'élément correspondant du store
  const element = testStore.testData?.elements?.find(el => el.el_ID === item.el_ID)
  if (element) {
    if (element.el_Text && element.el_Text.trim()) {
      return element.el_Text.substring(0, 100) + (element.el_Text.length > 100 ? '...' : '')
    }
    if (element.el_RichText && element.el_RichText.trim()) {
      return element.el_RichText.substring(0, 100) + (element.el_RichText.length > 100 ? '...' : '')
    }
  }
  
  // Fallback avec le type d'interaction
  const interactionType = item.type || 'default'
  return `${interactionType.charAt(0).toUpperCase() + interactionType.slice(1)} interaction - Click to edit`
}

// Méthodes drag & drop
const onDragStart = (event, item, levelIndex, itemIndex) => {
  console.log('🟡 Drag started:', {
    el_ID: item.el_ID,
    title: item.title,
    fromLevel: levelIndex + 1,
    position: itemIndex
  })
  
  draggedItem.value = item
  draggedFromLevel.value = levelIndex
  draggedFromIndex.value = itemIndex
  
  // Ajouter des données pour le transfert (utiliser el_ID maintenant)
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', item.el_ID)
}

const onDragOver = (event) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
}

const onDrop = (event, targetLevelIndex, targetItemIndex) => {
  event.preventDefault()
  
  if (!draggedItem.value) return
  
  console.log('🟢 Drop at level', targetLevelIndex + 1, 'position', targetItemIndex)
  
  // Ne pas faire de changement si on drop au même endroit
  if (draggedFromLevel.value === targetLevelIndex && draggedFromIndex.value === targetItemIndex) {
    console.log('⏭️ Dropped at same position, no change needed')
    return
  }
  
  // Avec l'architecture el_ID, déplacer l'el_ID entre les niveaux
  const el_ID_to_move = levels.value[draggedFromLevel.value].items.splice(draggedFromIndex.value, 1)[0]
  
  // Calculer la nouvelle position
  let newPosition = targetItemIndex
  
  // Si on déplace dans le même niveau et vers une position plus haute, ajuster l'index
  if (draggedFromLevel.value === targetLevelIndex && draggedFromIndex.value < targetItemIndex) {
    newPosition = targetItemIndex - 1
  }
  
  // Insérer l'el_ID à la nouvelle position
  levels.value[targetLevelIndex].items.splice(newPosition, 0, el_ID_to_move)
  
  console.log('✅ Item moved successfully:', {
    el_ID: el_ID_to_move,
    fromLevel: draggedFromLevel.value + 1,
    toLevel: targetLevelIndex + 1,
    newPosition
  })
  
  // Sauvegarder l'ordre dans le store
  saveItemOrder()
}

const onDragEnd = () => {
  console.log('🔚 Drag ended')
  draggedItem.value = null
  draggedFromLevel.value = -1
  draggedFromIndex.value = -1
}

// Méthode pour mettre à jour la difficulté d'un item CAT
const updateItemDifficulty = () => {
  if (!selectedItem.value || !selectedTestElement.value) return
  
  console.log(`🎯 Updating item difficulty: ${selectedItem.value.difficulty}`)
  
  // Mettre à jour la difficulté dans l'élément du test
  selectedTestElement.value.difficulty_rating = selectedItem.value.difficulty
  
  // Sauvegarder dans le testStore (cela déclenchera automatiquement la sync CAT)
  testStore.updateElement(selectedTestElement.value)
  
  // Sauvegarder l'ordre des items CAT
  saveItemOrder()
}

// Fonction pour sauvegarder la structure des niveaux dans testData.levels
const saveLevelsStructure = () => {
  console.log('💾 Saving levels structure to testData.levels...')
  
  // Créer la structure testData.levels (similaire aux pages)
  const levelsStructure = levels.value.map(level => ({
    _id: level.id,
    label: level.label || `Level ${level.level}`,
    items: level.items || [], // Array d'el_ID
    randomized: level.randomized || props.testData.CATParameters?.randomizedItemOrder || true
  }))
  
  console.log(`📊 Created levels structure with ${levelsStructure.length} levels:`, 
    levelsStructure.map(l => `${l.label} (${l.items.length} items)`).join(', '))
  
  return levelsStructure
}

// Fonction pour initialiser testData.levels lors de la création d'un nouveau test CAT
const initializeTestDataLevels = () => {
  const catParams = props.testData.CATParameters || { paliers: 4 }
  const numLevels = catParams.paliers || 4
  
  console.log('🆕 Initializing testData.levels for new CAT test with', numLevels, 'levels')
  
  // Créer la structure initiale des niveaux
  const initialLevels = Array.from({ length: numLevels }, (_, index) => ({
    _id: uuidv4(),
    label: `Level ${index + 1}`,
    items: [], // Array d'el_ID vide
    randomized: catParams.randomizedItemOrder || true
  }))
  
  console.log('✅ Created initial testData.levels structure:', 
    initialLevels.map(l => l.label).join(', '))
  
  return initialLevels
}

const saveItemOrder = async () => {
  console.log('💾 Saving item order and test data...')
  
  // FUSION des éléments : originaux (props) + nouveaux (testStore)
  const originalElements = props.testData.elements || []
  const newElements = testStore.testData?.elements || []
  
  console.log(`📊 Original elements: ${originalElements.length}`)
  console.log(`📊 New elements in testStore: ${newElements.length}`)
  
  // Filtrer les éléments supprimés des originaux
  const deletedElements = window.deletedCATElements || new Set()
  const filteredOriginalElements = originalElements.filter(el => !deletedElements.has(el.el_ID))
  
  if (deletedElements.size > 0) {
    console.log(`🗑️ Excluding ${deletedElements.size} deleted elements from fusion`)
  }
  
  // Fusionner sans doublons (basé sur el_ID)
  const allElements = [...filteredOriginalElements]
  let addedCount = 0
  
  newElements.forEach(newEl => {
    const existsInOriginal = allElements.some(existing => existing.el_ID === newEl.el_ID)
    if (!existsInOriginal) {
      allElements.push(newEl)
      addedCount++
      console.log(`➕ Added new element: ${newEl.el_ID} (${newEl.el_Type})`)
    } else {
      // Mettre à jour l'élément existant avec les nouvelles données
      const existingIndex = allElements.findIndex(existing => existing.el_ID === newEl.el_ID)
      if (existingIndex >= 0) {
        allElements[existingIndex] = { ...allElements[existingIndex], ...newEl }
        console.log(`🔄 Updated existing element: ${newEl.el_ID}`)
      }
    }
  })
  
  console.log(`📊 Final elements count: ${allElements.length} (${addedCount} added, ${originalElements.length} preserved)`)
  
  // Créer la structure des niveaux
  const levelsStructure = saveLevelsStructure()
  
  // Mettre à jour le store avec la fusion complète
  const updatedTestData = {
    ...props.testData,
    elements: allElements, // Fusion préservant les éléments existants
    levels: levelsStructure, // Nouvelle structure des niveaux (comme pages)
    // Conserver CATStructure pour rétrocompatibilité temporaire
    CATStructure: {
      levels: levels.value.map(level => ({
        id: level.id,
        level: level.level,
        difficulty: level.difficulty,
        items: level.items || []
      }))
    },
    metadata: {
      ...props.testData.metadata,
      LastModif: new Date().toISOString()
    }
  }
  
  console.log(`📊 Saving test with ${updatedTestData.elements?.length || 0} elements and ${levelsStructure.length} levels`)
  console.log(`📋 Levels structure:`, levelsStructure.map(l => `${l.label}: ${l.items.length} items`).join(', '))
  
  // Mettre à jour les niveaux dans le testStore
  testStore.updateCATLevels(levelsStructure)
  
  // Émettre vers le parent pour mise à jour du store
  emit('update:testData', updatedTestData)
  
  // Forcer aussi la sauvegarde dans le testStore pour synchroniser
  testStore.setTestData(updatedTestData)
  
  // CRITIQUE: Forcer la sauvegarde en base de données
  try {
    console.log('🔄 Forcing database save for CAT test...')
    const response = await api.post('/tests/save', updatedTestData)
    if (response.data) {
      console.log('✅ CAT test successfully saved to database with', updatedTestData.elements?.length, 'elements')
    }
  } catch (apiError) {
    console.error('❌ Error saving CAT test to database:', apiError)
  }
  
  // DIAGNOSTIC DETAILLE après sauvegarde
  console.log('\n🔍 POST-SAVE DIAGNOSTIC:')
  console.log('=======================')
  console.log('1. testStore.testData.elements:', testStore.testData?.elements?.length || 0)
  console.log('2. testStore.testData.levels:', testStore.testData?.levels?.length || 0)
  console.log('3. levels.value (interface):', levels.value?.length || 0)
  console.log('4. displayLevels.value:', displayLevels.value?.length || 0)
  
  // Vérifier chaque niveau
  levels.value?.forEach((level, index) => {
    const displayLevel = displayLevels.value?.[index]
    console.log(`Level ${index + 1} (${level.label}):`, {
      references: level.items?.length || 0,
      displayed: displayLevel?.items?.length || 0,
      firstRef: level.items?.[0],
      firstDisplay: displayLevel?.items?.[0]?.title
    })
  })
  
  console.log('=======================\n')
  
  // FORCE SYNC après sauvegarde pour corriger l'affichage
  const displayedItems = await forceSyncDisplay()
  
  if (displayedItems > 0) {
    console.log('✅ Item order and complete test data saved - DISPLAY SYNCHRONIZED')
  } else {
    console.warn('⚠️ Item order saved but display sync failed - manual refresh may be needed')
  }
}

// Watcher pour synchroniser le type d'interaction entre l'élément et l'item CAT
watch(
  () => selectedTestElement.value?.el_Type,
  (newType) => {
    if (selectedItem.value && newType && selectedItem.value.type !== newType) {
      console.log(`🔄 Updating CAT item type from ${selectedItem.value.type} to ${newType}`)
      
      // Mettre à jour le type dans l'item CAT
      selectedItem.value.type = newType
      
      // Mettre à jour l'item dans le niveau correspondant
      if (selectedLevelIndex.value >= 0 && selectedItemIndex.value >= 0) {
        levels.value[selectedLevelIndex.value].items[selectedItemIndex.value].type = newType
      }
      
      // Sauvegarder les changements
      saveItemOrder()
    }
  },
  { immediate: false }
)

// Surveiller les changements de CATDistribution (items distribués depuis la recherche)
watch(() => props.testData.CATDistribution, (newDistribution) => {
  if (newDistribution && newDistribution.items) {
    console.log("🎯 CATDistribution updated, reinitializing with distributed items...")
    initializeLevels()
  }
}, { deep: true })

// Surveiller les changements de CATParameters pour réinitialiser les niveaux
watch(() => props.testData.CATParameters, (newParams) => {
  if (newParams) {
    console.log("🔄 CATParameters changed, reinitializing levels")
    initializeLevels()
  }
}, { deep: true })

// Surveiller les changements de testData.levels pour forcer la réactivité
watch(() => props.testData.levels, (newLevels) => {
  if (newLevels && Array.isArray(newLevels) && newLevels.length > 0) {
    console.log('🔄 testData.levels changed, updating interface')
    
    // Mettre à jour levels.value depuis testData.levels
    levels.value = newLevels.map((level, index) => ({
      id: level._id,
      level: index + 1,
      difficulty: index + 1,
      label: level.label,
      randomized: level.randomized,
      items: level.items || []
    }))
    
    // Forcer la réactivité et activer le premier niveau
    nextTick(() => {
      if (levels.value.length > 0 && activeLevel.value === -1) {
        setActiveLevel(0)
      }
    })
  }
}, { deep: true, immediate: true })

// Surveiller les changements dans les éléments du test pour mettre à jour les vignettes
watch(() => testStore.testData?.elements, (newElements) => {
  if (!newElements || !Array.isArray(newElements)) return
  
  console.log('🔄 Test elements changed, updating CAT item cards...')
  
  // Parcourir tous les niveaux pour mettre à jour les vignettes
  levels.value.forEach((level, levelIndex) => {
    let levelUpdated = false
    
    level.items.forEach((item, itemIndex) => {
      // Trouver l'élément correspondant dans le test
      const correspondingElement = newElements.find(el => 
        el.el_ID === item.el_ID || 
        el.el_ID === item.catItemData?.itemEl_ID
      )
      
      if (correspondingElement) {
        const newTitle = correspondingElement.el_Text || correspondingElement.el_RichText
        const newType = correspondingElement.el_Type
        
        // Vérifier si une mise à jour est nécessaire
        if (newTitle && newTitle !== item.title) {
          console.log(`🔄 Updating item card: "${item.title}" → "${newTitle}"`)
          
          // Mettre à jour l'item
          const updatedItem = {
            ...item,
            title: newTitle,
            type: newType || item.type,
            el_Text: newTitle,
            contentPreview: getItemPreview({ title: newTitle, type: newType })
          }
          
          // Remplacer l'item dans le niveau
          level.items[itemIndex] = updatedItem
          levelUpdated = true
          
          // Si cet item est actuellement sélectionné, mettre à jour aussi la sélection
          if (selectedItem.value && selectedItem.value.id === item.id) {
            selectedItem.value = { ...updatedItem }
            console.log('🎯 Updated currently selected item as well')
          }
        }
      }
    })
    
    if (levelUpdated) {
      console.log(`✅ Level ${levelIndex + 1} cards updated`)
    }
  })
  
  // Forcer la réactivité Vue si des changements ont été effectués
  if (levels.value.some(level => level.items.some(item => 
    newElements.some(el => (el.el_ID === item.el_ID || el.el_ID === item.catItemData?.itemEl_ID) && 
                           (el.el_Text || el.el_RichText) !== item.title)
  ))) {
    console.log('🔄 Forcing Vue reactivity for card updates')
    levels.value = [...levels.value]
  }
}, { deep: true, immediate: false })

// Diagnostic automatique pour détecter les problèmes d'affichage
const checkDisplayIntegrity = () => {
  // Vérifier si on a des éléments mais aucun affichage
  const hasElements = testStore.testData?.elements?.length > 0
  const hasDisplayItems = displayLevels.value.some(level => level.items.length > 0)
  
  if (hasElements && !hasDisplayItems) {
    console.warn('⚠️ CAT Display Issue: Elements exist but not displayed')
    console.log('📊 Elements:', testStore.testData.elements.length)
    console.log('📊 Levels structure:', levels.value.map(l => `${l.label}: ${l.items?.length || 0} items`))
    console.log('🔄 Auto-fixing display...')
    
    // Auto-correction
    refreshDisplay()
  }
}

// Surveiller l'intégrité de l'affichage
watch(() => [testStore.testData?.elements?.length, displayLevels.value], () => {
  nextTick(() => {
    checkDisplayIntegrity()
  })
}, { deep: true })

// Initialisation au montage
onMounted(() => {
  initializeLevels()
  
  // Vérifier l'intégrité après initialisation
  nextTick(() => {
    checkDisplayIntegrity()
  })
  
  // Écouter les événements de mise à jour d'items CAT depuis l'Item Bank
  window.addEventListener('cat-item-updated', (event) => {
    const { itemId, newTitle, newPreview } = event.detail
    console.log('🎯 CATEditor received cat-item-updated event:', { itemId, newTitle, newPreview })
    
    // Parcourir tous les niveaux pour trouver l'item à mettre à jour
    levels.value.forEach((level, levelIndex) => {
      level.items.forEach((item, itemIndex) => {
        // Vérifier si c'est l'item à mettre à jour
        const isMatchingItem = 
          (item._id && item._id === itemId) ||
          (item.catItemData?._id && item.catItemData._id === itemId) ||
          (item.metadata?.OriginalItemId && item.metadata.OriginalItemId === itemId) ||
          (item.id && item.id === itemId)
        
        if (isMatchingItem) {
          console.log(`🔄 Updating CAT item card in Level ${levelIndex + 1}, Item ${itemIndex + 1}`)
          console.log(`   Title: "${item.title}" → "${newTitle}"`)
          
          // Mettre à jour les données de l'item
          const updatedItem = {
            ...item,
            title: newTitle,
            contentPreview: newPreview,
            el_Text: newTitle // Aussi mettre à jour el_Text si présent
          }
          
          // Remplacer l'item dans le niveau
          level.items[itemIndex] = updatedItem
          
          // Si cet item est actuellement sélectionné, mettre à jour la sélection
          if (selectedItem.value && 
              ((selectedItem.value._id && selectedItem.value._id === itemId) ||
               (selectedItem.value.id && selectedItem.value.id === itemId))) {
            console.log('🎯 Updating currently selected item')
            selectedItem.value = { ...updatedItem }
          }
          
          console.log('✅ CAT item card updated successfully')
        }
      })
    })
    
    // Forcer la réactivité Vue en recréant l'array des levels
    levels.value = [...levels.value]
  })
})

// Fonction pour forcer le rafraîchissement de l'affichage
const refreshDisplay = () => {
  console.log('🔄 Forcing CAT display refresh...')
  
  // Réinitialiser les niveaux
  initializeLevels()
  
  // Forcer la réactivité Vue
  nextTick(() => {
    levels.value = [...levels.value]
    
    // Activer le premier niveau s'il y a des items
    const hasItems = levels.value.some(level => level.items && level.items.length > 0)
    if (hasItems && activeLevel.value === -1) {
      setActiveLevel(0)
      console.log('🎯 First level activated, items should be visible')
    }
  })
}

// Fonction pour synchroniser immédiatement l'affichage après import/création
const forceSyncDisplay = async () => {
  console.log('🚀 Force sync display after import/creation...')
  
  // Étape 1: Vérifier les données
  const elementsCount = testStore.testData?.elements?.length || 0
  const levelsCount = props.testData?.levels?.length || 0
  
  console.log(`📊 Data check: ${elementsCount} elements, ${levelsCount} levels in testData`)
  
  if (elementsCount === 0) {
    console.log('⚠️ No elements found, cannot sync display')
    return
  }
  
  // Étape 2: Réinitialiser depuis testData.levels si elle existe
  if (props.testData.levels && props.testData.levels.length > 0) {
    console.log('🔄 Syncing from testData.levels...')
    
    levels.value = props.testData.levels.map((level, index) => ({
      id: level._id,
      level: index + 1,
      difficulty: index + 1,
      label: level.label,
      randomized: level.randomized,
      items: level.items || []
    }))
  } else {
    console.log('🔄 Rebuilding levels from elements...')
    initializeLevels()
  }
  
  // Étape 3: Forcer la réactivité et vérifier le résultat
  await nextTick()
  levels.value = [...levels.value]
  
  await nextTick()
  
  // Étape 4: Vérifier que displayLevels fonctionne
  const displayItemsCount = displayLevels.value.reduce((sum, level) => sum + level.items.length, 0)
  console.log(`🎯 Sync result: ${displayItemsCount} items displayed across ${displayLevels.value.length} levels`)
  
  // Étape 5: Activer le premier niveau avec des items
  const firstLevelWithItems = displayLevels.value.findIndex(level => level.items.length > 0)
  if (firstLevelWithItems >= 0) {
    setActiveLevel(firstLevelWithItems)
    console.log(`✅ Activated level ${firstLevelWithItems + 1} with ${displayLevels.value[firstLevelWithItems].items.length} items`)
  }
  
  return displayItemsCount
}

// Exposer des fonctions pour l'utilisation externe
defineExpose({
  initializeLevels,
  initializeTestDataLevels,
  saveLevelsStructure,
  refreshDisplay,
  forceSyncDisplay
})
</script>

<style scoped>
.cat-editor {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.no-test-selected {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.empty-state {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  max-width: 500px;
}

.cat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  gap: 20px;
}

.cat-main-info {
  flex: 1;
}

.cat-info .badge {
  margin-left: 10px;
}

.cat-requirements {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.requirement-info {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.requirement-legend {
  display: flex;
  gap: 15px;
  align-items: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
}

@media (max-width: 768px) {
  .cat-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .cat-requirements {
    align-items: flex-start;
  }
}

.levels-navigation {
  margin-bottom: 30px;
}

.levels-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.level-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.level-section.active-level {
  box-shadow: 0 4px 12px rgba(0,123,255,0.2);
  border: 2px solid #007bff;
}

.level-section.insufficient-items {
  background: linear-gradient(135deg, #fff5f5 0%, #fef2f2 100%);
  border-left: 4px solid #fca5a5;
}

.level-section.insufficient-items.active-level {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

.level-section.new-test-ready {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-left: 4px solid #3b82f6;
}

.level-section.new-test-ready.active-level {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border: 2px solid #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.level-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.level-header:hover {
  background-color: #f8f9fa;
}

.level-title h4 {
  margin: 0;
  color: #495057;
}

.level-difficulty {
  color: #6c757d;
  font-size: 0.9em;
  margin-left: 10px;
}

.level-stats {
  display: flex;
  align-items: center;
}

.item-count {
  color: #6c757d;
  font-size: 0.9em;
}

.item-count-ratio {
  font-weight: 600;
  font-size: 0.95em;
}

.level-warning {
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

.level-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.level-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.level-content {
  padding: 20px;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.item-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.item-card.selected-item {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

.item-card.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 100%;
  color: #6c757d;
  cursor: grab;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.drag-handle:hover {
  color: #007bff;
}

.drag-handle:active {
  cursor: grabbing;
}

.item-content-wrapper {
  flex: 1;
  min-width: 0;
}

.add-item-card {
  border: 2px dashed #dee2e6;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 150px;
}

.add-item-card:hover {
  border-color: #007bff;
  background: #e3f2fd;
}

.add-item-content {
  text-align: center;
  color: #6c757d;
}

.add-item-content i {
  margin-bottom: 10px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-type {
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  color: #495057;
}

.item-actions {
  display: flex;
  gap: 5px;
}

.item-preview h6 {
  margin: 0 0 5px 0;
  color: #495057;
}

.item-content {
  color: #6c757d;
  font-size: 0.9em;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
}

.no-items-message {
  text-align: center;
  color: #6c757d;
  padding: 40px 20px;
}

.no-items-message i {
  font-size: 2em;
  margin-bottom: 10px;
}

.success-state {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px dashed #22c55e;
  border-radius: 12px;
  padding: 30px;
  margin: 20px;
  color: #15803d;
}

.success-state i {
  font-size: 2.5em;
  margin-bottom: 15px;
}

.empty-level-state {
  opacity: 0.7;
}

.item-editor-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 30px;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.item-editor-placeholder {
  padding: 20px;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}
</style>